<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { Globe2 } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { NAV_ITEMS, type NavItem } from './sidebarNav'

const route = useRoute()
const authStore = useAuthStore()

const items = computed<NavItem[]>(() =>
  NAV_ITEMS.filter((item) => !item.adminOnly || authStore.isAdmin),
)

function isActive(to: string): boolean {
  if (to === '/') return route.path === '/'
  return route.path === to || route.path.startsWith(to + '/')
}
</script>

<template>
  <aside
    class="flex w-sidebar shrink-0 flex-col border-r border-border-primary bg-background-light"
  >
    <div
      class="flex h-header shrink-0 items-center gap-s border-b border-border-primary px-2xl"
    >
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-enable text-text-inverted"
      >
        <Globe2 :size="20" :stroke-width="2.25" />
      </span>
      <div class="flex min-w-0 flex-col leading-tight">
        <span class="truncate text-body-medium-semibold text-text-title">Talora Ops</span>
        <span class="truncate text-body-extra-small text-text-subtitle">Operator console</span>
      </div>
    </div>

    <nav class="flex flex-1 flex-col gap-2xxs overflow-y-auto p-s" aria-label="Primary">
      <RouterLink
        v-for="item in items"
        :key="item.to"
        :to="item.to"
        class="group flex min-h-[40px] items-center gap-s rounded-md px-s py-xs text-body-small-medium transition-colors"
        :class="
          isActive(item.to)
            ? 'bg-primary-accent text-text-brand-primary'
            : 'text-text-subtitle hover:bg-background-gray-light hover:text-text-title'
        "
        :aria-current="isActive(item.to) ? 'page' : undefined"
      >
        <component
          :is="item.icon"
          :size="20"
          :stroke-width="2"
          class="shrink-0 transition-colors"
          :class="
            isActive(item.to)
              ? 'text-icon-brand-primary'
              : 'text-icon-secondary group-hover:text-text-title'
          "
        />
        <span class="flex-1 truncate text-left">{{ item.label }}</span>
        <span
          v-if="item.comingSoon"
          class="shrink-0 rounded-full bg-background-gray-light px-xs py-px text-super-small font-medium uppercase tracking-wide text-text-subtitle"
        >
          Soon
        </span>
      </RouterLink>
    </nav>

    <div class="border-t border-border-primary px-2xl py-m">
      <p class="text-body-extra-small text-text-subtitle">
        Provisioning &amp; tenant operations
      </p>
    </div>
  </aside>
</template>
